import { useMemo } from 'react'
import { usePageTheme } from '../../contexts/page-theme-context'
import { Themes } from '../../contexts/section-theme-context'
import { BackgroundColor } from '../../lib/types/background'
import bgStyles from './bg-colors.module.scss'

export const getThemeFromBackgroundColor = (backgroundColor?: BackgroundColor, convertTo2024 = false): Themes => {
  if (!backgroundColor) {
    return Themes.Light
  }
  const bgColor = convertTo2024 ? bgTo2024Map[backgroundColor] : backgroundColor
  return darkThemeSet.has(bgColor) ? Themes.Dark : Themes.Light
}

const getStyle = (parentTheme: Themes, backgroundColor?: BackgroundColor, convertTo2024 = false): string[] => {
  if (!backgroundColor) {
    return []
  }
  if (backgroundColor === BackgroundColor.Transparent) {
    return [getTextColor(parentTheme)]
  }
  const bgColor = convertTo2024 ? bgTo2024Map[backgroundColor] : backgroundColor
  const theme = getThemeFromBackgroundColor(bgColor)
  return [getTextColor(theme), bgStyleMap[bgColor]]
}

export const useBackgroundStyle = (parentTheme: Themes, backgroundColor?: BackgroundColor): string[] => {
  const { color } = usePageTheme()
  return useMemo(
    () => getStyle(parentTheme, backgroundColor, color === 'convertTo2024'),
    [parentTheme, backgroundColor, color]
  )
}

const darkThemeSet: Set<BackgroundColor> = new Set<BackgroundColor>([
  BackgroundColor.N50,
  BackgroundColor.N60,
  BackgroundColor.N70,
  BackgroundColor.N80,
  BackgroundColor.N90,
  BackgroundColor.N100,

  BackgroundColor.G50,
  BackgroundColor.G60,
  BackgroundColor.G70,
  BackgroundColor.G80,
  BackgroundColor.G90,
  BackgroundColor.G100,

  BackgroundColor.B50,
  BackgroundColor.B60,
  BackgroundColor.B70,
  BackgroundColor.B80,
  BackgroundColor.B1001,

  BackgroundColor.C50,
  BackgroundColor.C60,

  BackgroundColor.I40,
  BackgroundColor.I50,
  BackgroundColor.I60,
  BackgroundColor.I70,
  BackgroundColor.I80,

  BackgroundColor.S50,
  BackgroundColor.S60,
  BackgroundColor.S70,
  BackgroundColor.S80,

  BackgroundColor.M60,
  BackgroundColor.M70,

  BackgroundColor.N100_24,

  BackgroundColor.G60_24,
  BackgroundColor.G100_24,

  BackgroundColor.B80_24,

  BackgroundColor.BT1001_24,

  BackgroundColor.P90_24,
  BackgroundColor.P100_24,

  BackgroundColor.T90_24,
])

const textStyleMap: { [key in Themes]: string } = {
  [Themes.Dark]: bgStyles.darkThemeTextColor,
  [Themes.Light]: bgStyles.lightThemeTextColor,
}

export const getTextColor = (theme: Themes): string => textStyleMap[theme]

const bgStyleMap: { [key in BackgroundColor]: string } = {
  [BackgroundColor.Transparent]: '',
  [BackgroundColor.N0]: bgStyles.n0,
  [BackgroundColor.N1]: bgStyles.n1,
  [BackgroundColor.N10]: bgStyles.n10,
  [BackgroundColor.N20]: bgStyles.n20,
  [BackgroundColor.N30]: bgStyles.n30,
  [BackgroundColor.N40]: bgStyles.n40,
  [BackgroundColor.N50]: bgStyles.n50,
  [BackgroundColor.N60]: bgStyles.n60,
  [BackgroundColor.N70]: bgStyles.n70,
  [BackgroundColor.N80]: bgStyles.n80,
  [BackgroundColor.N90]: bgStyles.n90,
  [BackgroundColor.N100]: bgStyles.n100,

  [BackgroundColor.G1]: bgStyles.g1,
  [BackgroundColor.G10]: bgStyles.g10,
  [BackgroundColor.G20]: bgStyles.g20,
  [BackgroundColor.G30]: bgStyles.g30,
  [BackgroundColor.G40]: bgStyles.g40,
  [BackgroundColor.G50]: bgStyles.g50,
  [BackgroundColor.G60]: bgStyles.g60,
  [BackgroundColor.G70]: bgStyles.g70,
  [BackgroundColor.G80]: bgStyles.g80,
  [BackgroundColor.G90]: bgStyles.g90,
  [BackgroundColor.G100]: bgStyles.g100,

  [BackgroundColor.B1]: bgStyles.b1,
  [BackgroundColor.B10]: bgStyles.b10,
  [BackgroundColor.B20]: bgStyles.b20,
  [BackgroundColor.B30]: bgStyles.b30,
  [BackgroundColor.B40]: bgStyles.b40,
  [BackgroundColor.B50]: bgStyles.b50,
  [BackgroundColor.B60]: bgStyles.b60,
  [BackgroundColor.B70]: bgStyles.b70,
  [BackgroundColor.B80]: bgStyles.b80,
  [BackgroundColor.B1001]: bgStyles.b1001,

  [BackgroundColor.C1]: bgStyles.c1,
  [BackgroundColor.C10]: bgStyles.c10,
  [BackgroundColor.C30]: bgStyles.c30,
  [BackgroundColor.C40]: bgStyles.c40,
  [BackgroundColor.C50]: bgStyles.c50,
  [BackgroundColor.C60]: bgStyles.c60,

  [BackgroundColor.YB1]: bgStyles.yb1,
  [BackgroundColor.YB10]: bgStyles.yb10,
  [BackgroundColor.YB20]: bgStyles.yb20,
  [BackgroundColor.YB30]: bgStyles.yb30,
  [BackgroundColor.YB40]: bgStyles.yb40,
  [BackgroundColor.YB50]: bgStyles.yb50,

  [BackgroundColor.I1]: bgStyles.i1,
  [BackgroundColor.I10]: bgStyles.i10,
  [BackgroundColor.I20]: bgStyles.i20,
  [BackgroundColor.I30]: bgStyles.i30,
  [BackgroundColor.I40]: bgStyles.i40,
  [BackgroundColor.I50]: bgStyles.i50,
  [BackgroundColor.I60]: bgStyles.i60,
  [BackgroundColor.I70]: bgStyles.i70,
  [BackgroundColor.I80]: bgStyles.i80,

  [BackgroundColor.S1]: bgStyles.s1,
  [BackgroundColor.S10]: bgStyles.s10,
  [BackgroundColor.S20]: bgStyles.s20,
  [BackgroundColor.S30]: bgStyles.s30,
  [BackgroundColor.S40]: bgStyles.s40,
  [BackgroundColor.S50]: bgStyles.s50,
  [BackgroundColor.S60]: bgStyles.s60,
  [BackgroundColor.S70]: bgStyles.s70,
  [BackgroundColor.S80]: bgStyles.s80,

  [BackgroundColor.M1]: bgStyles.m1,
  [BackgroundColor.M10]: bgStyles.m10,
  [BackgroundColor.M30]: bgStyles.m30,
  [BackgroundColor.M40]: bgStyles.m40,
  [BackgroundColor.M60]: bgStyles.m60,
  [BackgroundColor.M70]: bgStyles.m70,

  [BackgroundColor.L1]: bgStyles.l1,
  [BackgroundColor.L10]: bgStyles.l10,
  [BackgroundColor.L20]: bgStyles.l20,
  [BackgroundColor.L30]: bgStyles.l30,
  [BackgroundColor.L40]: bgStyles.l40,
  [BackgroundColor.L50]: bgStyles.l50,

  [BackgroundColor.W0_24]: bgStyles.w0_24,

  [BackgroundColor.N0_24]: bgStyles.n0_24,
  [BackgroundColor.N100_24]: bgStyles.n100_24,

  [BackgroundColor.G60_24]: bgStyles.g60_24,
  [BackgroundColor.G100_24]: bgStyles.g100_24,

  [BackgroundColor.BT1001_24]: bgStyles.bt1001_24,

  [BackgroundColor.O1001_24]: bgStyles.o1001_24,
  [BackgroundColor.O1002_24]: bgStyles.o1002_24,

  [BackgroundColor.B80_24]: bgStyles.b80_24,
  [BackgroundColor.B1001_24]: bgStyles.b1001_24,

  [BackgroundColor.T90_24]: bgStyles.t90_24,
  [BackgroundColor.P90_24]: bgStyles.p90_24,
  [BackgroundColor.P100_24]: bgStyles.p100_24,
}

export const bgTo2024Map: Record<BackgroundColor, BackgroundColor> = {
  [BackgroundColor.Transparent]: BackgroundColor.Transparent,
  [BackgroundColor.N0]: BackgroundColor.W0_24,
  [BackgroundColor.N1]: BackgroundColor.N0_24,
  [BackgroundColor.N10]: BackgroundColor.N0_24,
  [BackgroundColor.N20]: BackgroundColor.N0_24,
  [BackgroundColor.N30]: BackgroundColor.N0_24,
  [BackgroundColor.N40]: BackgroundColor.N0_24,
  [BackgroundColor.N50]: BackgroundColor.N100_24,
  [BackgroundColor.N60]: BackgroundColor.N100_24,
  [BackgroundColor.N70]: BackgroundColor.N100_24,
  [BackgroundColor.N80]: BackgroundColor.N100_24,
  [BackgroundColor.N90]: BackgroundColor.N100_24,
  [BackgroundColor.N100]: BackgroundColor.N100_24,

  [BackgroundColor.G1]: BackgroundColor.N0_24,
  [BackgroundColor.G10]: BackgroundColor.N0_24,
  [BackgroundColor.G20]: BackgroundColor.N0_24,
  [BackgroundColor.G30]: BackgroundColor.N0_24,
  [BackgroundColor.G40]: BackgroundColor.G60_24,
  [BackgroundColor.G50]: BackgroundColor.G60_24,
  [BackgroundColor.G60]: BackgroundColor.G60_24,
  [BackgroundColor.G70]: BackgroundColor.G60_24,
  [BackgroundColor.G80]: BackgroundColor.G100_24,
  [BackgroundColor.G90]: BackgroundColor.G100_24,
  [BackgroundColor.G100]: BackgroundColor.G100_24,

  [BackgroundColor.B1]: BackgroundColor.N0_24,
  [BackgroundColor.B10]: BackgroundColor.N0_24,
  [BackgroundColor.B20]: BackgroundColor.N0_24,
  [BackgroundColor.B30]: BackgroundColor.N0_24,
  [BackgroundColor.B40]: BackgroundColor.N0_24,
  [BackgroundColor.B50]: BackgroundColor.B80_24,
  [BackgroundColor.B60]: BackgroundColor.B80_24,
  [BackgroundColor.B70]: BackgroundColor.B80_24,
  [BackgroundColor.B80]: BackgroundColor.B80_24,
  [BackgroundColor.B1001]: BackgroundColor.B1001,

  [BackgroundColor.C1]: BackgroundColor.N0_24,
  [BackgroundColor.C10]: BackgroundColor.N0_24,
  [BackgroundColor.C30]: BackgroundColor.N0_24,
  [BackgroundColor.C40]: BackgroundColor.N0_24,
  [BackgroundColor.C50]: BackgroundColor.N100_24,
  [BackgroundColor.C60]: BackgroundColor.N100_24,

  [BackgroundColor.YB1]: BackgroundColor.N0_24,
  [BackgroundColor.YB10]: BackgroundColor.N0_24,
  [BackgroundColor.YB20]: BackgroundColor.N0_24,
  [BackgroundColor.YB30]: BackgroundColor.N0_24,
  [BackgroundColor.YB40]: BackgroundColor.N0_24,
  [BackgroundColor.YB50]: BackgroundColor.N0_24,

  [BackgroundColor.I1]: BackgroundColor.N0_24,
  [BackgroundColor.I10]: BackgroundColor.N0_24,
  [BackgroundColor.I20]: BackgroundColor.N0_24,
  [BackgroundColor.I30]: BackgroundColor.N0_24,
  [BackgroundColor.I40]: BackgroundColor.N0_24,
  [BackgroundColor.I50]: BackgroundColor.P90_24,
  [BackgroundColor.I60]: BackgroundColor.P90_24,
  [BackgroundColor.I70]: BackgroundColor.P90_24,
  [BackgroundColor.I80]: BackgroundColor.P100_24,

  [BackgroundColor.S1]: BackgroundColor.N0_24,
  [BackgroundColor.S10]: BackgroundColor.N0_24,
  [BackgroundColor.S20]: BackgroundColor.N0_24,
  [BackgroundColor.S30]: BackgroundColor.N0_24,
  [BackgroundColor.S40]: BackgroundColor.N0_24,
  [BackgroundColor.S50]: BackgroundColor.T90_24,
  [BackgroundColor.S60]: BackgroundColor.T90_24,
  [BackgroundColor.S70]: BackgroundColor.T90_24,
  [BackgroundColor.S80]: BackgroundColor.T90_24,

  [BackgroundColor.M1]: BackgroundColor.N0_24,
  [BackgroundColor.M10]: BackgroundColor.N0_24,
  [BackgroundColor.M30]: BackgroundColor.N0_24,
  [BackgroundColor.M40]: BackgroundColor.N0_24,
  [BackgroundColor.M60]: BackgroundColor.N100_24,
  [BackgroundColor.M70]: BackgroundColor.N100_24,

  [BackgroundColor.L1]: BackgroundColor.N0_24,
  [BackgroundColor.L10]: BackgroundColor.N0_24,
  [BackgroundColor.L20]: BackgroundColor.N0_24,
  [BackgroundColor.L30]: BackgroundColor.N0_24,
  [BackgroundColor.L40]: BackgroundColor.N0_24,
  [BackgroundColor.L50]: BackgroundColor.N0_24,

  [BackgroundColor.W0_24]: BackgroundColor.W0_24,

  [BackgroundColor.N0_24]: BackgroundColor.N0_24,
  [BackgroundColor.N100_24]: BackgroundColor.N100_24,

  [BackgroundColor.G60_24]: BackgroundColor.G60_24,
  [BackgroundColor.G100_24]: BackgroundColor.G100_24,

  [BackgroundColor.O1001_24]: BackgroundColor.O1001_24,
  [BackgroundColor.O1002_24]: BackgroundColor.O1002_24,

  [BackgroundColor.B80_24]: BackgroundColor.B80_24,
  [BackgroundColor.B1001_24]: BackgroundColor.B1001_24,

  [BackgroundColor.BT1001_24]: BackgroundColor.BT1001_24,

  [BackgroundColor.T90_24]: BackgroundColor.T90_24,
  [BackgroundColor.P90_24]: BackgroundColor.P90_24,
  [BackgroundColor.P100_24]: BackgroundColor.P100_24,
}
